import React from 'react';

import { DefaultContext } from '@apollo/client';
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions';
import { isNil } from 'es-toolkit';

import { mergeObjects } from '@hrlogics/helper-utils';

import { Nullable } from '@clearverify/types';

import {
  PeoCompanyType,
  PeoDocument,
  PeoQuery,
  PeoQueryVariables,
  PeoUpdate,
  PeoUpdateWithEmployersMutationVariables,
  usePeoQuery,
  usePeoUpdateMutation,
  usePeoUpdateWithEmployersMutation,
} from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useFileUpload } from 'src/hooks/file';
import { DroppedFile } from 'src/types/file';

export const usePeoById = (peoId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { uploadFile, uploading } = useFileUpload();

  const { data, loading } = usePeoQuery({
    variables: {
      id: String(peoId),
    },
    skip: !peoId,
  });

  const peo = React.useMemo(() => data?.peo, [data?.peo]);

  const [peoUpdateMutation, { loading: updatingPeo }] = usePeoUpdateMutation({
    context: createApolloContext({
      errorMessage: "Can't update Company Information",
      successMessage: 'Company Information has been successfully updated',
    }),
    onCompleted: (data, clientOptions) => {
      if (!peoId) return;
      const queryOptions = {
        query: PeoDocument,
        variables: {
          id: peoId,
        } satisfies PeoQueryVariables,
      };
      const queryData = clientOptions?.client?.readQuery<PeoQuery, PeoQueryVariables>(queryOptions);
      try {
        if (queryData) {
          clientOptions?.client?.writeQuery<PeoQuery, PeoQueryVariables>({
            ...queryOptions,
            data: mergeObjects<PeoQuery>(queryData, { peo: data.peoUpdate }),
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
  });

  const [updatePeoWithEmployersMutation, { loading: updatingPeoWithEmployers }] =
    usePeoUpdateWithEmployersMutation({
      context: createApolloContext({
        errorMessage: "Can't update Company Information",
        successMessage: 'Company Information has been successfully updated',
      }),
    });

  const updatePeo = React.useCallback(
    async (data: Omit<PeoUpdate, 'id'>, context?: DefaultContext) => {
      if (isNil(peoId)) {
        return;
      }

      await peoUpdateMutation({
        variables: {
          where: { id: peoId },
          data,
        },
        context,
      });
    },
    [peoId, peoUpdateMutation],
  );

  const updatePeoWithEmployers = React.useCallback(
    async (
      data: Omit<PeoUpdateWithEmployersMutationVariables, 'id'>,
      context?: DefaultContext,
      refetchQueries?: MutationBaseOptions['refetchQueries'],
    ) => {
      if (isNil(peoId)) {
        return;
      }

      await updatePeoWithEmployersMutation({
        variables: {
          id: peoId,
          ...data,
        },
        context,
        refetchQueries,
      });
    },
    [peoId, updatePeoWithEmployersMutation],
  );

  const updatePeoLogo = React.useCallback(
    async (logoFile: DroppedFile | string) => {
      // TODO: handle case with fileId
      if (typeof logoFile === 'string') throw new Error('Not implemented');
      if (!logoFile.file) return;

      try {
        const uploadedFile = await uploadFile(logoFile.file);
        await updatePeo({
          logo: {
            connect: {
              id: uploadedFile.id,
            },
          },
        });
      } catch (error) {
        console.error({ error });
      }
    },
    [uploadFile, updatePeo],
  );

  return {
    peo,
    peoId,
    updatePeo,
    updatePeoLogo,
    updatePeoWithEmployers,
    loading: loading,
    updatingPeo: updatingPeo || updatingPeoWithEmployers || uploading,
    isPartnership: peo?.companyType === PeoCompanyType.Partnership,
  };
};
